import { useEffect, useMemo, useState } from "react";
import useStore from "../../../../useStore";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../../Utilities/Fecha/fecha";
import "./ReporteSemanalRepartidor.css";

// selectedDate es un Date "de calendario" (mismo que usa CustomCalendar, ya en hora
// local del navegador) → lo formateamos con getters locales, sin tocar UTC.
const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtRangoCorto = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

// Muestra la semana (lunes–domingo) que contiene `selectedDate`, el mismo día
// que ya controla el calendario de "Mis pedidos asignados".
export default function ReporteSemanalRepartidor({ selectedDate }) {
  const {
    courierWeeklyEarnings,
    loadingCourierWeeklyEarnings,
    errorCourierWeeklyEarnings,
    fetchDataCourierWeeklyEarnings,
  } = useStore();

  const [showDetalle, setShowDetalle] = useState(false);

  const refDate = useMemo(
    () => toDateInput(selectedDate instanceof Date ? selectedDate : new Date()),
    [selectedDate]
  );

  useEffect(() => {
    fetchDataCourierWeeklyEarnings(refDate);
  }, [refDate, fetchDataCourierWeeklyEarnings]);

  const rango = courierWeeklyEarnings?.rango;
  const rangoLabel = useMemo(() => {
    if (!rango) return "";
    return `${fmtRangoCorto(rango.inicio)} – ${fmtRangoCorto(rango.fin)}`;
  }, [rango]);

  return (
    <div className="rsr-panel card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="rsr-title mb-0">🛵 Cuánto tengo que cobrar esta semana</h5>
          <span className="rsr-rango">{rangoLabel || "…"}</span>
        </div>

        {loadingCourierWeeklyEarnings && (
          <div className="text-center py-3 text-muted">Cargando…</div>
        )}

        {!loadingCourierWeeklyEarnings && !!errorCourierWeeklyEarnings && (
          <div className="alert alert-danger">{errorCourierWeeklyEarnings}</div>
        )}

        {!loadingCourierWeeklyEarnings && !errorCourierWeeklyEarnings && courierWeeklyEarnings && (
          <>
            {/* 💰 Lo importante: quién le debe pagar qué */}
            <div className="row g-3 mb-2">
              <div className="col-12 col-md-6">
                <div className="rsr-stat rsr-stat--admin">
                  <span className="rsr-stat-label">
                    Te debe pagar el admin (Mercado Pago) — {courierWeeklyEarnings.porCobrarAdmin?.entregas || 0} entrega(s)
                  </span>
                  <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.porCobrarAdmin?.total)}</span>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="rsr-stat rsr-stat--cash">
                  <span className="rsr-stat-label">
                    Ya cobraste en efectivo — {courierWeeklyEarnings.cobradoEnEfectivo?.entregas || 0} entrega(s)
                  </span>
                  <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.cobradoEnEfectivo?.total)}</span>
                </div>
              </div>
            </div>

            {courierWeeklyEarnings.pendienteConfirmacionMp?.entregas > 0 && (
              <div className="alert alert-warning py-2 px-3 mb-3 small">
                ⏳ {courierWeeklyEarnings.pendienteConfirmacionMp.entregas} entrega(s) en Mercado Pago
                ({fmtCurrency(courierWeeklyEarnings.pendienteConfirmacionMp.total)}) todavía sin confirmar el pago
                — no cuentan como "por cobrar" hasta que Mercado Pago lo confirme.
              </div>
            )}

            <div className="row g-3">
              <div className="col-6 col-md-3">
                <div className="rsr-stat rsr-stat--total">
                  <span className="rsr-stat-label">Total (efectivo + admin)</span>
                  <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.resumen?.total)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rsr-stat rsr-stat--entregas">
                  <span className="rsr-stat-label">Entregas ({courierWeeklyEarnings.resumen?.entregas || 0})</span>
                  <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.resumen?.porEntregas)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rsr-stat rsr-stat--propinas">
                  <span className="rsr-stat-label">Propinas</span>
                  <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.resumen?.propinas)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rsr-stat rsr-stat--abandon">
                  <span className="rsr-stat-label">No entregados ({courierWeeklyEarnings.abandonados?.cantidad || 0})</span>
                  <span className="rsr-stat-value">$0</span>
                </div>
              </div>
            </div>

            {courierWeeklyEarnings.pedidosEntregados?.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowDetalle((v) => !v)}
                >
                  {showDetalle ? "Ocultar" : "Ver"} detalle de entregas ({courierWeeklyEarnings.pedidosEntregados.length})
                </button>

                {showDetalle && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm rsr-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Negocio</th>
                          <th>Método</th>
                          <th>¿Quién paga?</th>
                          <th>Entrega</th>
                          <th>Propina</th>
                          <th>Total</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courierWeeklyEarnings.pedidosEntregados.map((p) => {
                          const esMp = (p.paymentMethod || "").toLowerCase() === "mercadopago";
                          return (
                            <tr key={p.orderId}>
                              <td><code>{p.orderId}</code></td>
                              <td>{p.restauranteNombre || "-"}</td>
                              <td>{p.paymentMethod || "-"}</td>
                              <td>
                                <span className={`badge bg-${esMp ? "warning" : "success"}`}>
                                  {esMp ? "Admin" : "Ya cobrado"}
                                </span>
                              </td>
                              <td>{fmtCurrency(p.pagoEntrega)}</td>
                              <td>{fmtCurrency(p.propina)}</td>
                              <td><strong>{fmtCurrency(p.total)}</strong></td>
                              <td>{fmtDate(p.createdAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {(courierWeeklyEarnings.leDebeAlAdmin?.pedidos > 0) && (
              <div className="rsr-debo mt-3">
                <h6 className="rsr-debo-title">
                  ⚠️ Ojo: en efectivo cobraste TODO el pedido — la comisión de envío no es tuya, se la debes al admin
                </h6>
                <div className="row g-3">
                  <div className="col-12">
                    <div className="rsr-stat rsr-stat--debo">
                      <span className="rsr-stat-label">
                        Le debes al admin — {courierWeeklyEarnings.leDebeAlAdmin.pedidos} pedido(s) × ${courierWeeklyEarnings.leDebeAlAdmin.comisionEnvio}
                      </span>
                      <span className="rsr-stat-value">{fmtCurrency(courierWeeklyEarnings.leDebeAlAdmin.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="rsr-footnote mt-3 mb-0">
              ${courierWeeklyEarnings.pagoPorEntrega} fijos por cada pedido entregado + 100% de la propina.
              Si el pedido fue en efectivo, ya lo cobraste tú directo del cliente (pero también trajiste la
              comisión de envío del admin, mira arriba). Si fue Mercado Pago, te lo debe transferir el admin.
              Los pedidos no entregados (cliente no salió) no generan cobro.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
